import React from "react";

import moment from "moment";

import { IActivityMetaDetails } from "@Models/IActivityDetails";
import { dateFormatList, isDevelopment, isPresent } from "@Utils";
import { CheckOutlined, CloseSquareOutlined } from "@ant-design/icons";
import { Checkbox, DatePicker, Form, FormInstance, Input, Select, Typography } from "antd";
import IconButton from "@Components/shared/IconButton";
import useQuestionnaireSelection from "@Components/activity/hooks/useQuestionnaireSelection";

interface IProps {
    canEditDate?: boolean;
    className?: string;
    disabled?: boolean;
    hasButtons?: boolean;
    initialValues: Partial<IActivityMetaDetails & { creationDate: moment.Moment }>;
    isUpdating?: boolean;
    onClose?: () => void;
    onUpdate?: (_, changedValues: IActivityMetaDetails & { creationDate?: string }) => void;
    onValuesChange?: (
        changedValues: Partial<IActivityMetaDetails & { creationDate: string }>,
        values: IActivityMetaDetails & { creationDate?: string }
    ) => Promise<void> | void;
    form?: FormInstance;
}
export const EditActivityMetadata: React.FC<IProps> = ({
    canEditDate = false,
    className = "",
    disabled = false,
    form: parentForm,
    hasButtons,
    initialValues,
    isUpdating = false,

    onClose,
    onUpdate,
    onValuesChange,
}) => {
    const [formInstance] = Form.useForm<IActivityMetaDetails>();
    const form = formInstance ?? parentForm;
    const { canShowSelect } = useQuestionnaireSelection();

    const handleValuesChange = (
        changedValues: Partial<IActivityMetaDetails & { creationDate: string }>,
        values: IActivityMetaDetails & { creationDate?: string }
    ) => {
        const { creationDate, ...model } = values;
        const changedValueKeys = Object.keys(changedValues);
        if (changedValueKeys.includes("creationDate") && changedValueKeys.length === 1 && creationDate === null) {
            // don't send a request when creationDate is cleared, even though the BE ignores this request
            return null;
        }
        if (changedValueKeys.includes("tags") && changedValues.tags.length) {
            if (changedValues.tags[changedValues.tags.length - 1]?.trim() === "") {
                changedValues.tags.pop();
            }
        }

        // only send the creation date value if it's changed and isn't blank
        return onValuesChange?.(changedValues, {
            ...model,
            activityInstanceId: initialValues.activityInstanceId ?? model.activityInstanceId,
            creationDate: changedValues.creationDate,
        });
    };

    const handleUpdate = () => {
        if (isPresent(initialValues.activityInstanceId)) {
            const values: IActivityMetaDetails & { creationDate?: string } = {
                ...form?.getFieldsValue(),
                activityInstanceId: initialValues.activityInstanceId,
            };

            // the creation date can't be changed at this stage
            const { creationDate, ...model } = values;
            onUpdate?.({}, model);
        }
    };

    const formattedInitialValues = {
        ...initialValues,
        creationDate: initialValues.creationDate ? moment(initialValues.creationDate) : null,
        tags: initialValues.tags?.length ? initialValues.tags.filter((tag) => tag.trim() !== "") : initialValues.tags,
    };

    return (
        <Form
            className={className}
            form={form}
            initialValues={formattedInitialValues}
            labelAlign="left"
            labelCol={{ xs: 24, sm: 8, md: 10, xl: 6 }}
            onValuesChange={handleValuesChange}
            style={{ padding: "10px 20px" }}
        >
            <Typography.Title level={5} style={{ textAlign: "center" }}>
                Additional Details (optional)
            </Typography.Title>
            <Form.Item name="title" label="Title">
                <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
                <Input.TextArea />
            </Form.Item>
            <Form.Item
                name="creationDate"
                label="Date Recorded"
                tooltip={!canEditDate ? "Date recorded cannot be changed." : null}
            >
                <DatePicker
                    format={dateFormatList}
                    disabled={!canEditDate}
                    disabledDate={(current) =>
                        // Can not select days after today
                        current > moment().endOf("day")
                    }
                />
            </Form.Item>
            <Form.Item name="tags" label="Tags">
                <Select mode="tags">
                    {formattedInitialValues?.tags?.map((item, index) => (
                        <Select.Option key={index} value={item}>
                            {item}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            {isDevelopment() ? (
                <Form.Item
                    label="Make Public"
                    name="isPublic"
                    tooltip={"This will make the video available to everyone on the platform."}
                    valuePropName="checked"
                >
                    <Checkbox />
                </Form.Item>
            ) : null}

            {canShowSelect ? (
                <Form.Item
                    label="Make CME Public"
                    name="isCMEPublic"
                    tooltip={
                        "This will allow anyone with access to the video able to earn cme points from this activity."
                    }
                    valuePropName="checked"
                >
                    <Checkbox />
                </Form.Item>
            ) : null}
            {!!onUpdate && hasButtons ? (
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <Form.Item>
                        <IconButton
                            className="secondary"
                            disabled={disabled}
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isUpdating}
                            onClick={handleUpdate}
                            style={{ marginRight: "10px" }}
                        >
                            Update
                        </IconButton>
                    </Form.Item>
                    {onClose ? (
                        <Form.Item>
                            <IconButton
                                className="secondary danger"
                                disabled={disabled}
                                icon={<CloseSquareOutlined />}
                                onClick={onClose}
                            >
                                Close
                            </IconButton>
                        </Form.Item>
                    ) : null}
                </div>
            ) : null}
        </Form>
    );
};
